import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { ArrowLeft, Send, Sparkles } from "lucide-react";
import TraceSheet, { renderWithTapToTrace } from "@/components/TraceSheet";

export default function Coach() {
  const nav = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [traceAmount, setTraceAmount] = useState(null);
  const scrollRef = useRef();

  useEffect(() => {
    (async () => {
      const [{ data: sug }, { data: hist }] = await Promise.all([
        api.get("/coach/suggestions"),
        api.get("/coach/history"),
      ]);
      setSuggestions(sug);
      if (hist.length) {
        // Use last session
        const lastSession = hist[hist.length - 1].session_id;
        setSessionId(lastSession);
        setMessages(hist.filter((m) => m.session_id === lastSession));
      }
    })();
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = async (text) => {
    if (!text.trim() || busy) return;
    setBusy(true);
    setInput("");
    const userMsg = { role: "user", content: text };
    setMessages((m) => [...m, userMsg, { role: "assistant", content: "" }]);

    try {
      const token = localStorage.getItem("mw_token");
      const res = await fetch(`${API}/coach/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      if (!res.ok || !res.body) throw new Error("Stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data:")) continue;
          const json = part.slice(5).trim();
          if (!json) continue;
          try {
            const ev = JSON.parse(json);
            if (ev.delta) {
              setMessages((m) => {
                const clone = [...m];
                clone[clone.length - 1] = { ...clone[clone.length - 1], content: clone[clone.length - 1].content + ev.delta };
                return clone;
              });
            } else if (ev.session_id && !sessionId) {
              setSessionId(ev.session_id);
            }
          } catch {}
        }
      }
    } catch (e) {
      setMessages((m) => {
        const clone = [...m];
        clone[clone.length - 1] = { role: "assistant", content: "Sorry — I couldn't get an answer just now. Please try again." };
        return clone;
      });
    }
    setBusy(false);
  };

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <div className="sticky top-0 z-10 backdrop-blur-xl bg-white/85 border-b border-slate-200 px-6 py-4 flex items-center gap-3 max-w-md mx-auto w-full">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors" aria-label="Back" data-testid="coach-back-btn">
          <ArrowLeft size={18} strokeWidth={1.75} />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-2xl bg-slate-900 text-white flex items-center justify-center">
            <Sparkles size={16} className="text-emerald-400" strokeWidth={2} />
          </div>
          <div>
            <div className="font-manrope font-bold text-slate-900 tracking-tight leading-none">Money Coach</div>
            <div className="text-[11px] text-slate-500">Ask about your money</div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 pt-4 pb-32 max-w-md mx-auto w-full space-y-4">
        {messages.length === 0 && (
          <div>
            <p className="text-slate-600 text-sm leading-relaxed">Hey — I'm your Money Coach. I read your real spending to answer questions. Try one of these:</p>
            <div className="mt-4 space-y-2">
              {suggestions.map((s, i) => (
                <button key={i} data-testid={`coach-suggestion-${i}`} onClick={() => send(s)} className="w-full text-left rounded-2xl border border-slate-200 hover:border-slate-900 hover:bg-slate-50 transition-colors px-4 py-3 text-sm text-slate-800">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`coach-msg-${m.role}-${i}`}>
            <div className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-slate-900 text-white rounded-br-md" : "bg-slate-100 text-slate-900 rounded-bl-md"}`}>
              {m.role === "assistant"
                ? (m.content ? renderWithTapToTrace(m.content, (v) => setTraceAmount(v)) : (busy && i === messages.length - 1 ? <TypingDots /> : "…"))
                : m.content}
            </div>
          </div>
        ))}
      </div>

      <TraceSheet amount={traceAmount} onClose={() => setTraceAmount(null)} />

      {/* Input */}
      <div className="fixed bottom-0 left-0 right-0 backdrop-blur-xl bg-white/90 border-t border-slate-200 max-w-md mx-auto">
        <form
          onSubmit={(e) => { e.preventDefault(); send(input); }}
          className="px-6 py-4 flex items-center gap-2"
        >
          <input
            data-testid="coach-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about your money…"
            disabled={busy}
            className="flex-1 bg-slate-100 border-0 rounded-full px-5 py-3 text-sm outline-none focus:ring-2 focus:ring-slate-900 transition-colors duration-200"
          />
          <button data-testid="coach-send-btn" type="submit" disabled={busy || !input.trim()} className="w-11 h-11 rounded-full bg-slate-900 text-white flex items-center justify-center hover:bg-slate-800 active:scale-95 transition-transform duration-150 disabled:opacity-40">
            <Send size={16} strokeWidth={2} />
          </button>
        </form>
        <p className="text-[10px] text-slate-400 text-center pb-3 px-6">MoneyWise Coach uses your data to answer. Not financial advice.</p>
      </div>
    </div>
  );
}

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" />
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" style={{ animationDelay: "150ms" }} />
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" style={{ animationDelay: "300ms" }} />
    </span>
  );
}
